/**
 * Colyseus Game Client Service
 * Handles connection to the backend multiplayer game rooms
 */

import * as Colyseus from 'colyseus.js';
import config from '../config/environment';
import { dbg } from '../utils/debugLogger';
import type { 
  GameState, 
  Card,
  SuitType
} from '../types/game';

// Define the events locally since backend uses enums
export const GameEvent = {
  GAME_STATE_UPDATED: 'game_state_updated',
  PLAYER_JOINED: 'player_joined',
  PLAYER_LEFT: 'player_left',
  PLAYER_CONVERTED_TO_AI: 'player_converted_to_ai',
  ROUND_STARTED: 'round_started',
  BIDDING_STARTED: 'bidding_started',
  BID_PLACED: 'bid_placed',
  BIDDING_ENDED: 'bidding_ended',
  TRICK_STARTED: 'trick_started',
  CARD_PLAYED: 'card_played',
  TRICK_COMPLETED: 'trick_completed',
  PARTNER_REVEALED: 'partner_revealed',
  ROUND_ENDED: 'round_ended',
  GAME_ENDED: 'game_ended',
  ERROR: 'error'
} as const;

export type GameEventType = typeof GameEvent[keyof typeof GameEvent];

export interface GameEventPayload {
  event: GameEventType;
  data: any;
  timestamp: Date;
}

export interface RoomState {
  status: string;
  phase: string;
  playerCount: number;
  currentPlayer: string;
  gameId: string;
}

export interface JoinOptions {
  userId: string;
  username: string;
}

export interface GameClientCallbacks {
  onGameStateUpdate: (gameState: GameState) => void;
  onGameEvent: (event: GameEventPayload) => void;
  onError: (error: string) => void;
  onRoomStateUpdate: (state: RoomState) => void;
  onPlayerJoined: (data: { playerId: string; username: string; playerCount: number }) => void;
  onPlayerLeft: (data: { playerId: string }) => void;
  onPlayerConvertedToAI: (data: { playerId: string }) => void;
  onRoomScoresUpdate: (scores: any) => void;
}

export class GameClient {
  private client: Colyseus.Client;
  private room: Colyseus.Room<RoomState> | null = null;
  private callbacks: Partial<GameClientCallbacks> = {};
  private currentUserId: string = '';
  private frontendRoomId: string | null = null; // Track the frontend room ID for score synchronization

  constructor(serverUrl: string = config.api.wsUrl) {
    // Force the correct WebSocket URL for development to avoid any proxy issues
    const finalUrl = import.meta.env.DEV 
      ? 'ws://localhost:3001'  // Direct connection in development
      : serverUrl;
    
  // constructor debug logs (muted for clean scoring traces) -> runtime-toggleable
  dbg('network', '🔗 GameClient initializing with WebSocket URL:', finalUrl);
  dbg('network', '🔗 Config wsUrl:', config.api.wsUrl);
  dbg('network', '🔗 VITE_WS_URL env var:', import.meta.env.VITE_WS_URL);
  dbg('network', '🔗 Development mode:', import.meta.env.DEV);
    this.client = new Colyseus.Client(finalUrl);
  }

  /**
   * Register callbacks for game events
   */
  public setCallbacks(callbacks: Partial<GameClientCallbacks>) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Set the frontend room ID for score synchronization
   */
  public setFrontendRoomId(roomId: string) {
    this.frontendRoomId = roomId;
    dbg('room-sync', 'Set frontend room ID for score sync', { frontendRoomId: roomId });
  }

  /**
   * Join or create a Kadi ni Tidi game room
   */
  public async joinKadiNiTidiRoom(options: JoinOptions, enableAI: boolean = false): Promise<void> {
    try {
  dbg('network', '🔄 Attempting to connect to game server...', { options, enableAI });
      this.currentUserId = options.userId;
      
      // Try to join existing room first, create if none available
      this.room = await this.client.joinOrCreate<RoomState>('kadi-ni-tidi', {
        ...options,
        enableAI
      });
      
  dbg('network', '🎮 Connected to Kadi ni Tidi room:', this.room.roomId);
      
      this.setupRoomEventHandlers();
      
    } catch (error) {
      console.error('❌ Failed to join room:', error);
      this.callbacks.onError?.(`Failed to connect to game room: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  /**
   * Join a specific room by ID (or create if it doesn't exist)
   */
  public async joinRoomById(roomId: string, options: JoinOptions): Promise<void> {
    try {
  dbg('network', '🔄 Attempting to join specific room...', { roomId, options });
      this.currentUserId = options.userId;
      
      // First, fetch room info from backend to get maxPlayers and partnershipConfig
      let roomMaxPlayers = 4; // default
      let partnershipConfig: string | undefined = undefined;

      // ALWAYS fetch from database as source of truth (don't rely solely on cache)
      try {
        const roomResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/matchmaking/rooms`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        
        if (roomResponse.ok) {
          const roomData = await roomResponse.json();
          // The API returns { success: true, data: { rooms: [...], totalRooms: n } }
          const rooms = roomData.data?.rooms || roomData.data || [];
          const foundRoom = rooms.find((room: any) => room.roomId === roomId);
          if (foundRoom) {
            if (foundRoom.maxPlayers) {
              roomMaxPlayers = foundRoom.maxPlayers;
              dbg('network', `🎯 Found database room maxPlayers: ${roomMaxPlayers} for room ${roomId}`);
            }
            if (foundRoom.settings?.partnershipConfig) {
              partnershipConfig = foundRoom.settings.partnershipConfig;
              dbg('network', `🎯 Found partnership config: ${partnershipConfig} for room ${roomId}`);
            }
          } else {
            dbg('network', `🔍 Room ${roomId} not found in available rooms, using default maxPlayers: ${roomMaxPlayers}`);
          }
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch room info from database, checking cache fallback:', error);
        
        // Fallback to cache only if database fetch fails
        try {
          const cached = localStorage.getItem(`room_settings_${roomId}`);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (typeof parsed?.maxPlayers === 'number') {
              roomMaxPlayers = parsed.maxPlayers;
              dbg('network', `🎯 Using cached room settings maxPlayers=${roomMaxPlayers} for room ${roomId}`);
            }
            if (parsed?.partnershipConfig) {
              partnershipConfig = parsed.partnershipConfig;
              dbg('network', `🎯 Using cached partnershipConfig=${partnershipConfig} for room ${roomId}`);
            }
          }
        } catch (e) {
          console.warn('⚠️ Failed to parse cached room settings:', e);
        }
      }
      
      // Instead of joinById, use joinOrCreate with specific roomId as metadata
      // This allows Colyseus to create the room if it doesn't exist
      this.room = await this.client.joinOrCreate<RoomState>('kadi-ni-tidi', {
        ...options,
        frontendRoomId: roomId, // Pass the frontend room ID as metadata
        maxPlayers: roomMaxPlayers, // Pass the correct maxPlayers to Colyseus room
        partnershipConfig: partnershipConfig // Pass partnership config for partner count enforcement
      });

      // Optionally clear the one-time cached settings to avoid stale reuse
      try {
        localStorage.removeItem(`room_settings_${roomId}`);
      } catch {}
      
  dbg('network', '🔗 Joined/created room:', roomId, 'Colyseus room ID:', this.room.roomId);
      
      this.setupRoomEventHandlers();
      
    } catch (error) {
      console.error('❌ Failed to join room by ID:', error);
      this.callbacks.onError?.(`Failed to join room ${roomId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  /**
   * Request AI players to fill empty slots (legacy method)
   */
  public requestAIPlayers(): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot request AI players: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

  dbg('network', '🤖 Requesting AI players');
      this.room.send('request_ai_players', {
        playerId: this.currentUserId
      });
    } catch (error) {
      const errorMessage = `Failed to request AI players: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error requesting AI players:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Add a single AI player to the room
   */
  public addAIPlayer(): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot add AI player: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

      dbg('network', '🤖 Adding AI player');
      this.room.send('add_ai_player', {
        playerId: this.currentUserId
      });
    } catch (error) {
      const errorMessage = `Failed to add AI player: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error adding AI player:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Remove an AI player from the room
   */
  public removeAIPlayer(): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot remove AI player: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

      dbg('network', '🤖 Removing AI player');
      this.room.send('remove_ai_player', {
        playerId: this.currentUserId
      });
    } catch (error) {
      const errorMessage = `Failed to remove AI player: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error removing AI player:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Leave the current game room
   */
  public async leaveRoom(): Promise<void> {
    if (this.room) {
      try {
        await this.room.leave();
        this.room = null;
  dbg('network', '👋 Left game room');
      } catch (error) {
        console.error('❌ Error leaving room:', error);
      }
    }
  }

  /**
   * Send a bid to the server
   */
  public sendBid(bidAmount: number, powerHouse: SuitType, partnerCards: Card[]): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot send bid: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

      // Validate bid data - skip validation here since backend will handle it
      // Frontend UI already validates with correct ranges from shared utils
  // console.log('📤 Sending bid:', { bidAmount, powerHouse, partnerCards: partnerCards.length });

      if (!partnerCards || !Array.isArray(partnerCards) || partnerCards.length === 0) {
        const error = 'Invalid partner cards provided';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

      // Validate each partner card
      for (const card of partnerCards) {
        if (!card || !card.suit || !card.rank) {
          const error = 'Invalid partner card in array';
          console.error('❌', error);
          this.callbacks.onError?.(error);
          return;
        }
      }

  // console.log('💰 Sending bid:', { bidAmount, powerHouse, partnerCards });

      this.room.send('bid', {
        playerId: this.currentUserId,
        bidAmount,
        powerHouse,
        partnerCards
      });
    } catch (error) {
      const errorMessage = `Failed to send bid: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error sending bid:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Send a pass action to the server
   */
  public sendPass(): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot send pass: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

  // console.log('🚫 Sending pass message to server:', { playerId: this.currentUserId });
      this.room.send('pass', {
        playerId: this.currentUserId
      });
    } catch (error) {
      const errorMessage = `Failed to pass: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error sending pass:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Test WebSocket connectivity
   */
  private testConnection(): void {
    if (!this.room) {
      console.error('❌ No room connection for connectivity test');
      return;
    }

    try {
  // console.log('🔍 Testing WebSocket connection...');
      // Send a test message to verify connectivity
      this.room.send('test_connection', { timestamp: Date.now() });
  // console.log('✅ Test connection message sent');
    } catch (error) {
      console.error('❌ WebSocket connectivity test failed:', error);
      // Try to reconnect if connection is broken
      this.handleConnectionError();
    }
  }

  /**
   * Handle connection errors
   */
  private handleConnectionError(): void {
    console.warn('🔄 Attempting to reconnect due to connection issues...');
    this.callbacks.onError?.('Connection lost. Attempting to reconnect...');
    // Implement reconnection logic here if needed
  }

  /**
   * Play a card
   */
  public playCard(card: Card): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot play card: Not connected to room or no user ID';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

  /* console.log('🃏🃏🃏 FRONTEND DEBUG: playCard called with:', {
        playerId: this.currentUserId,
        card: {
          id: card.id,
          rank: card.rank,
          suit: card.suit
        }
      });

  console.log block muted:
  /* console.log('🔗🔗🔗 WEBSOCKET CONNECTION STATUS:', {
        roomExists: !!this.room,
        roomId: this.room?.roomId,
        sessionId: this.room?.sessionId,
        isConnected: this.isConnected(),
        roomState: this.room?.state
      }); */

      // Test connection before sending critical message
      this.testConnection();

  dbg('network', '📤📤📤 FRONTEND: About to send play_card message to backend');
      this.room.send('play_card', {
        playerId: this.currentUserId,
        card
      });

  dbg('network', '✅✅✅ FRONTEND: Card play message sent to backend successfully');
    } catch (error) {
      const errorMessage = `Failed to play card: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌❌❌ ERROR PLAYING CARD:', error);
      console.error('❌ WebSocket send failed - connection may be broken');
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Set player ready status
   */
  public setReady(ready: boolean): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot set ready: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

  dbg('network', '🔄 Setting ready status:', ready);
      this.room.send('ready', {
        playerId: this.currentUserId,
        ready
      });
    } catch (error) {
      const errorMessage = `Failed to set ready status: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error setting ready:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Restart game with same players
   */
  public restartGame(): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot restart game: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

  dbg('network', '🔄 Requesting game restart');
      this.room.send('restart_game', {
        playerId: this.currentUserId
      });
    } catch (error) {
      const errorMessage = `Failed to restart game: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error restarting game:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Toggle auto-play mode for current player
   */
  public toggleAutoPlay(enabled: boolean): void {
    try {
      if (!this.room || !this.currentUserId) {
        const error = 'Cannot toggle auto-play: Not connected to room';
        console.error('❌', error);
        this.callbacks.onError?.(error);
        return;
      }

      dbg('network', `🤖 Toggling auto-play: ${enabled ? 'ON' : 'OFF'}`);
      this.room.send('toggle_auto_play', {
        playerId: this.currentUserId,
        enabled: enabled
      });
    } catch (error) {
      const errorMessage = `Failed to toggle auto-play: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error('❌ Error toggling auto-play:', error);
      this.callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Get current room information
   */
  public getRoomInfo(): { roomId: string; playerCount: number; frontendRoomId: string | null } | null {
    if (!this.room) return null;
    
    return {
      roomId: this.room.roomId,
      playerCount: this.room.state.playerCount,
      frontendRoomId: this.frontendRoomId
    };
  }

  /**
   * Get partnership configuration from room state
   */
  public getPartnershipConfig(): string | undefined {
    if (!this.room?.state) return undefined;
    return (this.room.state as any).partnershipConfig || undefined;
  }

  /**
   * Check if connected to a room
   */
  public isConnected(): boolean {
    return this.room !== null;
  }

  /**
   * Get current user ID
   */
  public getCurrentUserId(): string {
    return this.currentUserId;
  }

  /**
   * Setup event handlers for the room
   */
  private setupRoomEventHandlers(): void {
    if (!this.room) return;

    // Room state changes
    this.room.onStateChange((state: RoomState) => {
  dbg('network', '🔄 Room state updated:', state);
      this.callbacks.onRoomStateUpdate?.(state);
     
       // Track AI engine status changes
       if ((state as any).aiEngineStatus) {
         const aiStatus = (state as any).aiEngineStatus;
         dbg('ai', '🤖 AI Engine Status:', aiStatus);
         window.dispatchEvent(new CustomEvent('aiEngineStatusChanged', { 
           detail: aiStatus 
         }));
       }
    });

    // Game state updates
    this.room.onMessage('game_state', (gameState: GameState) => {
  dbg('network', '🎮 Game state updated:', gameState);
  dbg('network', '📊 Game state debug:', {
        phase: gameState.phase,
        hasCurrentRound: !!gameState.currentRound,
        gameHistoryLength: gameState.gameHistory?.length || 0,
        lastRoundScoring: gameState.gameHistory?.[gameState.gameHistory.length - 1]?.scoring
      });

      // Enhanced scoring debug - preserved via dbg so it can be enabled with localStorage
      if (gameState.currentRound?.scoring) {
        dbg('scoring', 'Current Round Scoring Debug', {
          biddingTeamPoints: gameState.currentRound.scoring.biddingTeam.pointsWon,
          opposingTeamPoints: gameState.currentRound.scoring.opposingTeam.pointsWon,
          tricksCompleted: gameState.currentRound.playPhase?.completedTricks?.length || 0,
          partnerRevealed: gameState.currentRound.playPhase?.partnerRevealed,
          individualScores: gameState.currentRound.scoring.individualScores,
          tricksWonByPlayer: gameState.currentRound.scoring.tricksWonByPlayer
        });
      }

      // Cumulative scores debug
      if ((gameState as any).cumulativeTeamScores) {
        dbg('scoring', 'Cumulative Scores Debug', (gameState as any).cumulativeTeamScores);
      }

      // Trick transition debug
      if (gameState.currentRound?.playPhase) {
        dbg('scoring', 'Trick Transition Debug', {
          currentTrick: gameState.currentRound.playPhase.currentTrick ? {
            cards: gameState.currentRound.playPhase.currentTrick.cards.length,
            isComplete: gameState.currentRound.playPhase.currentTrick.isComplete
          } : null,
          completedTricks: gameState.currentRound.playPhase.completedTricks.length,
          currentPlayer: gameState.currentRound.playPhase.currentPlayer
        });
      }

      this.callbacks.onGameStateUpdate?.(gameState);
    });

    // Game events (player actions, etc.)
    this.room.onMessage('game_event', (event: GameEventPayload) => {
  dbg('network', '🎯 Game event:', event);
      this.callbacks.onGameEvent?.(event);
      
      // Handle specific events
      if (event.event === GameEvent.PLAYER_JOINED) {
        this.callbacks.onPlayerJoined?.(event.data);
      } else if (event.event === GameEvent.PLAYER_LEFT) {
        this.callbacks.onPlayerLeft?.(event.data);
      } else if (event.event === GameEvent.PLAYER_CONVERTED_TO_AI) {
        this.callbacks.onPlayerConvertedToAI?.(event.data);
      } else if (event.event === GameEvent.GAME_ENDED) {
        // Sync game completion with frontend room ID
        this.syncGameCompletionScores(event.data);
      }
    });

    // Error handling
    this.room.onMessage('error', (error: { message: string }) => {
      // Don't treat "You are now ready" messages as errors - they're informational
      if (error.message?.includes('You are now ready')) {
        console.log('✅ Ready status:', error.message);
        // Optionally call a different callback if needed
        return;
      }
      console.error('🚨 Game error:', error.message);
      this.callbacks.onError?.(error.message);
    });

    // Test connection response
    this.room.onMessage('connection_test_response', (response: { timestamp: number, serverTime: number, status: string }) => {
      const roundTripTime = Date.now() - response.timestamp;
  dbg('network', '✅ WebSocket connectivity confirmed - RTT:', roundTripTime + 'ms', 'Status:', response.status);
    });

    // Room scores updated (for leaderboard)
    this.room.onMessage('roomScoresUpdated', (scores: any) => {
      dbg('scoring', 'Room scores updated', scores);
      
      // Forward to any listening components via a custom event
      // This allows components like RoomRanking to receive the updates
      window.dispatchEvent(new CustomEvent('roomScoresUpdated', { detail: scores }));
      
      // Also store in callback if needed
      this.callbacks.onRoomScoresUpdate?.(scores);
    });

    // Turn timer started (for phase-specific countdown)
    this.room.onMessage('turn_timer_started', (data: { 
      playerId: string; 
      startTime: number; 
      durationMs: number;
      phase: string;
    }) => {
      dbg('network', '⏰ Turn timer started:', data);
      
      // Forward to components via custom event
      window.dispatchEvent(new CustomEvent('turnTimerStarted', { detail: data }));
    });

    // Connection error
    this.room.onError((code: number, message?: string) => {
      console.error('🔌 Connection error:', code, message);
      this.callbacks.onError?.(`Connection error: ${message || 'Unknown error'}`);
    });

    // Room left
    this.room.onLeave((code: number) => {
  dbg('network', '👋 Left room with code:', code);
      this.room = null;
    });
  }

    /**
   * Reconnect to a specific room (for reconnection after disconnect)
   */
  public async reconnectToRoom(roomId: string, options: JoinOptions): Promise<void> {
    try {
      this.currentUserId = options.userId;
      // For reconnection, we'll try to join the specific room again
      this.room = await this.client.joinById<RoomState>(roomId, options);
      
  dbg('network', '🔄 Reconnected to room:', roomId);
      this.setupRoomEventHandlers();
      
    } catch (error) {
      console.error('❌ Failed to reconnect:', error);
      this.callbacks.onError?.('Failed to reconnect to game');
      throw error;
    }
  }

  /**
   * Get available rooms (for lobby/matchmaking)
   */
  public async getAvailableRooms(): Promise<any[]> {
    try {
      // For now, return empty array since we'll use joinOrCreate
      // In a real implementation, you'd query your backend API
      return [];
    } catch (error) {
      console.error('❌ Failed to get available rooms:', error);
      return [];
    }
  }

  /**
   * Sync game completion scores with frontend room ID
   */
  private async syncGameCompletionScores(gameData: any): Promise<void> {
    console.log('🏆 syncGameCompletionScores called with frontendRoomId:', this.frontendRoomId, 'gameData:', gameData);
    
    if (!this.frontendRoomId) {
      console.warn('⚠️ No frontend room ID set for score synchronization');
      dbg('room-sync', 'No frontend room ID set for score synchronization');
      return;
    }

    try {
      console.log('🏆 Syncing game completion scores with frontend room:', this.frontendRoomId);
      dbg('room-sync', 'Syncing game completion scores with frontend room', { frontendRoomId: this.frontendRoomId, gameDataSummary: { gameNumber: gameData.gameNumber, players: (gameData.players || []).length } });

      // Import the roomScoreApi dynamically to avoid circular imports
      const { roomScoreApi } = await import('./roomScoreApi');
      
      // Transform game data to the expected format for room score API
      const gameCompletionData = {
        gameNumber: gameData.gameNumber || 1,
        finalScores: {
          biddingTeam: gameData.biddingTeamScore || 0,
          opposingTeam: gameData.opposingTeamScore || 0
        },
        winningTeam: gameData.winningTeam || 'bidding',
        players: gameData.players || [],
        totalRounds: gameData.totalRounds || 1,
        duration: gameData.duration,
        gameSettings: {
          ...gameData.gameSettings
        }
      };

      console.log('🏆 Recording game completion with data:', gameCompletionData);
      
      // Record the game completion in the frontend room
      const success = await roomScoreApi.recordGameCompletion(this.frontendRoomId, gameCompletionData);
      
      if (success) {
        console.log('✅ Successfully synced game completion scores with frontend room');
        dbg('room-sync', '✅ Successfully synced game completion scores with frontend room');
      } else {
        console.warn('⚠️ Failed to sync game completion scores with frontend room');
        dbg('room-sync', '⚠️ Failed to sync game completion scores with frontend room');
      }
      
    } catch (error) {
      console.error('❌ Error syncing game completion scores:', error);
    }
  }
}

// Create singleton instance
export const gameClient = new GameClient();
